import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Smartphone, Star, Trash2, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { authenticatedFetch, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { RemontaApk } from "@shared/schema";

function formatBytes(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Apks() {
  const { profile, session, loading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const isOwner = profile?.roles?.includes("owner") ?? false;

  const [versao, setVersao] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: apksList, isLoading } = useQuery<RemontaApk[]>({
    queryKey: ["/api/owner/apks"],
    queryFn: async () => {
      const res = await authenticatedFetch("/api/owner/apks");
      if (!res.ok) throw new Error("Erro ao carregar APKs");
      return res.json();
    },
    enabled: !!session?.access_token && isOwner,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const file = fileInputRef.current?.files?.[0];
      if (!file) throw new Error("Selecione o arquivo .apk");
      if (file.size > 50 * 1024 * 1024) throw new Error('O APK deve ter até 50 MB. Use uma versão release do App.');
      if (!versao.trim()) throw new Error("Informe a versão");

      const params = new URLSearchParams({
        versao: versao.trim(),
        nomeArquivo: file.name,
      });
      const res = await authenticatedFetch(`/api/owner/apks?${params}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.android.package-archive",
        },
        body: file,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao enviar APK");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/apks"] });
      setVersao("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast({ title: "APK enviado com sucesso" });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const setPrincipalMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authenticatedFetch(`/api/owner/apks/${id}/principal`, {
        method: "PUT",
      });
      if (!res.ok) throw new Error("Erro ao definir como principal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/apks"] });
      toast({ title: "Versão principal atualizada" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authenticatedFetch(`/api/owner/apks/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Erro ao excluir APK");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/apks"] });
      toast({ title: "APK removido" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!isOwner) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">Gerenciar APKs</h1>
        <p className="text-muted-foreground mt-1">
          Envie novas versões do app Android e escolha qual fica disponível no botão "Baixar o App"
        </p>
      </div>

      <Card className="border-none shadow-md max-w-2xl mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-900">Enviar nova versão</CardTitle>
          <CardDescription className="text-xs">
            Arquivo .apk assinado, pronto para instalação, até 50 MB
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="versao" className="text-xs">Versão</Label>
            <Input
              id="versao"
              placeholder="ex: 1.4.0"
              value={versao}
              onChange={(e) => setVersao(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="apk-file" className="text-xs">Arquivo .apk</Label>
            <Input id="apk-file" type="file" accept=".apk" ref={fileInputRef} />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => uploadMutation.mutate()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
              )}
              Enviar
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="max-w-2xl space-y-3">
        {isLoading ? (
          Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
        ) : !apksList || apksList.length === 0 ? (
          <Card className="border-none shadow-md">
            <CardContent className="py-12 text-center text-slate-500">
              <Smartphone className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhum APK enviado ainda</p>
            </CardContent>
          </Card>
        ) : (
          apksList.map((apk) => (
            <Card key={apk.id} className="border-none shadow-md">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Smartphone className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">v{apk.versao}</p>
                      {apk.principal && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Principal</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {apk.nomeArquivo} · {formatBytes(apk.tamanhoBytes)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!apk.principal && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPrincipalMutation.mutate(apk.id)}
                      disabled={setPrincipalMutation.isPending}
                    >
                      <Star className="w-3.5 h-3.5 mr-1.5" />
                      Tornar principal
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate(apk.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </Shell>
  );
}
